import React, { useEffect, useState, useMemo } from 'react';
import api from '../../api';

// --- Icon System ---
const Icons = {
  Close: () => <svg className="w-6 h-6 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>,
  Search: () => <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" /></svg>,
  UserAdd: () => <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" /></svg>,
  UserRemove: () => <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7a4 4 0 11-8 0 4 4 0 018 0zM9 14a6 6 0 00-6 6v1h12v-1a6 6 0 00-6-6zM21 12h-6" /></svg>,
  Check: () => <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>,
  Filter: () => <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" /></svg>,
  Users: () => <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z" /></svg>,
};

const SubjectFacultyAssignment = ({
  isOpen = false,
  subject = null,
  department = null,
  onClose = () => {},
  onAssign = async (subject, ids) => {}
}) => {
  // --- State ---
  const [faculty, setFaculty] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [viewMode, setViewMode] = useState('all');

  // --- Effects ---
  useEffect(() => {
    if (!isOpen || !department || !subject) return;

    const loadData = async () => {
      setLoading(true);
      setError('');
      try {
        const token = localStorage.getItem('token');
        const headers = token ? { Authorization: `Bearer ${token}` } : {};

        const [deptRes, assignedRes] = await Promise.all([
          api.get(`/api/departments/${department.tenantId}/${department._id}/faculty`, { headers }),
          api.get(`/api/subjects/get-faculty-by-subject/${subject._id}/${department.tenantId}`, { headers })
        ]);
        
        const deptFaculty = Array.isArray(deptRes?.data?.faculty) ? deptRes.data.faculty : [];
        const assigned = Array.isArray(assignedRes?.data?.faculty) ? assignedRes.data.faculty : [];
        const assignedIds = new Set(assigned.map(f => f._id));

        const facultyWithStatus = deptFaculty.map(f => ({
          _id: f._id || f.id,
          name: f.name || f.fullName || 'Unknown Faculty',
          email: f.email || '',
          avatar: `https://ui-avatars.com/api/?name=${encodeURIComponent(f.name || 'U')}&background=random&color=fff&size=128`,
          department: f.department || department.name,
          isAssigned: assignedIds.has(f._id || f.id)
        }));

        setFaculty(facultyWithStatus);
      } catch (err) {
        console.error('Failed to load faculty:', err);
        setError('Unable to load faculty roster. Please check your connection.');
      } finally {
        setLoading(false);
      }
    };

    loadData();
    setSelectedIds(new Set());
    setSuccess('');
    setSearchTerm('');
  }, [isOpen, subject, department]);

  // --- Memoization ---
  const filteredFaculty = useMemo(() => {
    return faculty.filter(f => {
      if (viewMode === 'assigned' && !f.isAssigned) return false;
      if (viewMode === 'available' && f.isAssigned) return false;
      
      const q = (searchTerm || '').toLowerCase().trim();
      if (!q) return true;
      return (f.name || '').toLowerCase().includes(q) || (f.email || '').toLowerCase().includes(q);
    });
  }, [faculty, viewMode, searchTerm]);

  const stats = useMemo(() => ({
    total: faculty.length,
    assigned: faculty.filter(f => f.isAssigned).length,
    available: faculty.filter(f => !f.isAssigned).length,
    selected: selectedIds.size
  }), [faculty, selectedIds]);

  // --- Handlers ---
  const toggleAssignment = async (facultyMember) => {
    if (saving) return;
    setSaving(true);
    setError('');
    const facultyId = facultyMember._id;
    const subjectId = subject?._id || subject?.id;
    const token = localStorage.getItem('token');
    const headers = token ? { Authorization: `Bearer ${token}` } : undefined;
    const original = [...faculty];

    try {
      setFaculty(prev => prev.map(f => f._id === facultyId ? { ...f, isAssigned: !f.isAssigned } : f));
      const endpoint = facultyMember.isAssigned ? 'remove-faculty' : 'assign-faculty';
      const payload = { facultyId, tenantId: department?.tenantId, departmentId: department?._id };

      try {
         await api.put(`/api/subjects/${endpoint}/${subjectId}`, payload, { headers });
      } catch (err) {
        if (err?.response?.status === 400 || err?.response?.status === 404) {
           await api.put(`/api/subjects/${endpoint}/${subjectId}`, { ...payload, facultyIds: [facultyId] }, { headers });
        } else throw err;
      }

      const newList = faculty.map(f => f._id === facultyId ? { ...f, isAssigned: !f.isAssigned } : f);
      const updatedAssignedIds = newList.filter(f => f.isAssigned).map(f => f._id);
      if (onAssign) await onAssign(subject, updatedAssignedIds);
      
      setSuccess(`${facultyMember.name} ${facultyMember.isAssigned ? 'removed' : 'assigned'}.`);
      setTimeout(() => setSuccess(''), 2500);
    } catch (err) {
      setFaculty(original);
      setError('Action failed. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const toggleSelect = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === filteredFaculty.length && filteredFaculty.length > 0) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(filteredFaculty.map(f => f._id)));
    }
  };

  const handleBulkAction = async (actionType) => {
    if (saving || stats.selected === 0) return;
    setSaving(true);
    setError('');
    
    const ids = Array.from(selectedIds);
    const subjectId = subject?._id || subject?.id;
    const token = localStorage.getItem('token');
    const headers = token ? { Authorization: `Bearer ${token}` } : undefined;
    const endpoint = actionType === 'assign' ? 'assign-faculty' : 'remove-faculty';

    try {
      const payload = { facultyIds: ids, tenantId: department?.tenantId, departmentId: department?._id };

      try {
        await api.put(`/api/subjects/${endpoint}/${subjectId}`, payload, { headers });
      } catch (err) {
        if (err?.response?.status === 400 || err?.response?.status === 404) {
          for (const id of ids) {
            await api.put(`/api/subjects/${endpoint}/${subjectId}`, { ...payload, facultyId: id }, { headers });
          }
        } else throw err;
      }

      setFaculty(prev => prev.map(f => ids.includes(f._id) ? { ...f, isAssigned: actionType === 'assign' } : f));
      const updatedList = faculty.map(f => ids.includes(f._id) ? { ...f, isAssigned: actionType === 'assign' } : f);
      const assignedIds = updatedList.filter(f => f.isAssigned).map(f => f._id);
      if (onAssign) onAssign(subject, assignedIds);

      setSuccess(`Successfully ${actionType === 'assign' ? 'assigned' : 'removed'} ${ids.length} faculty.`);
      setSelectedIds(new Set());
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error(err);
      setError(`Bulk ${actionType} failed.`);
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen || !subject) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4 bg-slate-900/60 backdrop-blur-xs transition-all">
      <div className="bg-white w-full h-[100dvh] sm:h-[90vh] sm:max-w-5xl flex flex-col overflow-hidden sm:rounded-2xl shadow-2xl border-none sm:border border-slate-200">
        
        {/* --- Header --- */}
        <div className="bg-slate-50 border-b border-slate-200 p-4 sm:p-6 flex flex-row justify-between items-center gap-4 flex-shrink-0">
          <div className="min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <span className="px-2.5 py-0.5 rounded-full bg-blue-100 text-blue-700 text-[10px] sm:text-xs font-bold uppercase tracking-wide">
                Subject Staffing
              </span>
              {department?.name && <span className="text-slate-400 text-xs sm:text-sm truncate hidden xs:inline">• {department.name}</span>}
            </div>
            <h2 className="text-lg sm:text-2xl font-bold text-slate-800 leading-tight truncate pr-2">{subject.name}</h2>
            <div className="text-xs text-slate-500 mt-0.5 hidden sm:block">Manage course instructors and faculty allocation</div>
          </div>
          
          <button 
            onClick={onClose} 
            className="p-2 -mr-2 rounded-lg hover:bg-slate-200 text-slate-400 hover:text-slate-600 transition flex-shrink-0"
            aria-label="Close configuration modal"
          >
            <Icons.Close />
          </button>
        </div>

        {/* --- Controls Toolbar --- */}
        <div className="p-4 bg-white border-b border-slate-100 space-y-3 flex-shrink-0">
          
          {/* Top Control Block: Grid Tabs & Keyword Input Search */}
          <div className="flex flex-col md:flex-row gap-3 justify-between md:items-center">
            {/* Horizontal scrollable tab buttons pool on cellular platforms */}
            <div className="flex p-1 bg-slate-100 rounded-xl overflow-x-auto whitespace-nowrap no-scrollbar max-w-full">
              {[
                { id: 'all', label: 'All Faculty', count: stats.total },
                { id: 'assigned', label: 'Assigned', count: stats.assigned },
                { id: 'available', label: 'Available', count: stats.available }
              ].map(tab => (
                <button
                  key={tab.id}
                  type="button"
                  onClick={() => setViewMode(tab.id)}
                  className={`px-3.5 py-2 sm:py-1.5 text-xs sm:text-sm font-semibold rounded-lg transition-all flex items-center gap-1.5 ${
                    viewMode === tab.id
                      ? 'bg-white text-blue-600 shadow-xs'
                      : 'text-slate-500 hover:text-slate-800'
                  }`}
                >
                  <span>{tab.label}</span>
                  <span className="text-[10px] opacity-70 bg-slate-200/60 px-1.5 py-0.5 rounded-md font-bold">{tab.count}</span>
                </button>
              ))}
            </div>

            {/* Keyword Input Form */}
            <div className="relative w-full md:w-72">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-slate-400">
                <Icons.Search />
              </div>
              <input
                type="text"
                className="block w-full pl-9 pr-3 py-3 sm:py-2 border border-slate-300 rounded-lg text-sm bg-white text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                placeholder="Search by name or email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>

          {/* Bottom Bulk Toolbar Row */}
          <div className="flex items-center justify-between bg-slate-50 p-2 rounded-xl border border-slate-100 min-h-[44px]">
            <label className="flex items-center px-2 cursor-pointer select-none">
              <input
                type="checkbox"
                className="w-5 h-5 sm:w-4 sm:h-4 text-blue-600 border-slate-300 rounded focus:ring-blue-500 cursor-pointer"
                checked={filteredFaculty.length > 0 && selectedIds.size === filteredFaculty.length}
                onChange={toggleSelectAll}
                disabled={loading || filteredFaculty.length === 0}
              />
              <span className="ml-2.5 text-xs sm:text-sm text-slate-600 font-semibold">Select All Visible</span>
            </label>

            <div className="flex items-center gap-2">
              {stats.selected > 0 && (
                <>
                  <span className="text-xs text-slate-500 hidden xs:inline font-medium mr-1">{stats.selected} selected</span>
                  <button
                    type="button"
                    onClick={() => handleBulkAction('assign')}
                    disabled={saving}
                    className="flex items-center gap-1 px-3 py-2 sm:py-1.5 bg-blue-600 text-white text-[11px] font-bold uppercase tracking-wider rounded-lg hover:bg-blue-700 disabled:opacity-50 transition"
                  >
                    <Icons.UserAdd /> Assign
                  </button>
                  <button
                    type="button"
                    onClick={() => handleBulkAction('remove')}
                    disabled={saving}
                    className="flex items-center gap-1 px-3 py-2 sm:py-1.5 bg-white border border-red-200 text-red-600 text-[11px] font-bold uppercase tracking-wider rounded-lg hover:bg-red-50 disabled:opacity-50 transition"
                  >
                    <Icons.UserRemove /> Remove
                  </button>
                </>
              )}
            </div>
          </div>
        </div>

        {/* --- Alert Messages Strip --- */}
        {(error || success) && (
          <div className={`px-4 py-2 text-xs sm:text-sm font-semibold text-center border-b ${
            error ? 'bg-red-50 text-red-600 border-red-100' : 'bg-green-50 text-green-600 border-green-100'
          }`}>
            {error || success}
          </div>
        )}

        {/* --- Dynamic Content Area --- */}
        <div className="flex-1 overflow-y-auto p-4 sm:p-6 bg-slate-50/50 min-h-0">
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {[1, 2, 3, 4, 5, 6].map((i) => (
                <div key={i} className="bg-white p-4 rounded-xl border border-slate-200 shadow-xs animate-pulse flex items-center gap-4">
                  <div className="w-12 h-12 bg-slate-200 rounded-full flex-shrink-0"></div>
                  <div className="flex-1 space-y-2">
                    <div className="h-4 bg-slate-200 rounded w-3/4"></div>
                    <div className="h-3 bg-slate-200 rounded w-1/2"></div>
                  </div>
                </div>
              ))}
            </div>
          ) : filteredFaculty.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-slate-400 p-8 text-center">
              <div className="p-3 bg-white rounded-full border border-slate-100 shadow-xs mb-3">
                <Icons.Users />
              </div>
              <p className="text-base font-semibold text-slate-700">No faculty members found</p>
              <p className="text-xs text-slate-400 mt-1">Try adapting your keyword query or navigation view tabs.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 pb-8">
              {filteredFaculty.map((f) => {
                const isSelected = selectedIds.has(f._id);
                return (
                  <label
                    key={f._id}
                    className={`relative bg-white rounded-xl border transition-all select-none cursor-pointer p-4 flex items-start gap-3.5 ${
                      isSelected 
                        ? 'border-blue-400 ring-1 ring-blue-400 bg-blue-50/10 shadow-xs' 
                        : f.isAssigned 
                          ? 'border-emerald-200 bg-emerald-50/20' 
                          : 'border-slate-200 hover:border-slate-300 hover:shadow-xs'
                    }`}
                  >
                    {/* Checkbox Trigger Wrapper */}
                    <div className="pt-0.5 flex-shrink-0">
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => toggleSelect(f._id)}
                        onClick={(e) => e.stopPropagation()}
                        className="w-5 h-5 sm:w-4 sm:h-4 text-blue-600 border-slate-300 rounded focus:ring-blue-500 cursor-pointer"
                      />
                    </div>

                    {/* Faculty Profile Avatar */}
                    <div className="relative flex-shrink-0">
                      <img 
                        src={f.avatar} 
                        alt={f.name} 
                        className={`w-11 h-11 rounded-full object-cover border-2 ${f.isAssigned ? 'border-emerald-400' : 'border-slate-100'}`} 
                      />
                      {f.isAssigned && (
                        <div className="absolute -bottom-1 -right-1 bg-emerald-500 text-white rounded-full p-0.5 border-2 border-white flex items-center justify-center">
                          <svg className="w-2.5 h-2.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={3}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                          </svg>
                        </div>
                      )}
                    </div>

                    {/* Information Text Block */}
                    <div className="flex-1 min-w-0">
                      <h4 className={`font-semibold text-sm truncate ${f.isAssigned ? 'text-emerald-950' : 'text-slate-900'}`}>
                        {f.name}
                      </h4>
                      <p className="text-xs text-slate-500 truncate mt-0.5">{f.email}</p>
                      
                      {/* Interactive Trigger Footer inside Cards */}
                      <div className="mt-3 flex items-center justify-between gap-2">
                        <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-md ${
                          f.isAssigned ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-500'
                        }`}>
                          {f.isAssigned ? 'Assigned' : 'Available'}
                        </span>
                         
                        <button
                          type="button"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            toggleAssignment(f);
                          }}
                          disabled={saving}
                          className={`text-xs font-bold px-2.5 py-1 rounded-lg transition ${
                            f.isAssigned 
                              ? 'text-red-600 bg-red-50 hover:bg-red-100' 
                              : 'text-blue-600 bg-blue-50 hover:bg-blue-100'
                          }`}
                        >
                          {f.isAssigned ? 'Revoke' : 'Assign'}
                        </button>
                      </div>
                    </div>
                  </label>
                );
              })}
            </div>
          )}
        </div>
        
        {/* --- Sticky Footer Window --- */}
        <div className="bg-white border-t border-slate-200 p-4 flex justify-end flex-shrink-0">
          <button 
            type="button"
            onClick={onClose}
            className="w-full sm:w-auto px-6 py-3 sm:py-2 text-sm font-semibold text-slate-700 bg-slate-50 border border-slate-300 rounded-lg hover:bg-slate-100 transition shadow-xs text-center"
          >
            Done
          </button>
        </div>

      </div>
    </div>
  );
};

export default SubjectFacultyAssignment;